
import { DOMAnimations } from "../utils/dom"

class AccordionBlock {
  constructor(el) {
    this.el = el
    this.items = []
    this.options = {
      autoclose: false,
      animationDuration: 350
    }

    this.init()
  }

  init() {
    if (!this.el) return

    this.parseRootOptions()

    const accordionItems = Array.from(
      this.el.querySelectorAll(".wp-block-accordion-item")
    )

    if (!accordionItems.length) {
      return
    }

    this.el.removeAttribute("data-wp-interactive")

    accordionItems.forEach((itemEl, index) => {
      const button = itemEl.querySelector(".wp-block-accordion-heading__toggle")
      const panel = itemEl.querySelector(".wp-block-accordion-panel")

      if (!button || !panel) return

      const icon = button.querySelector(
        ".wp-block-accordion-heading__toggle-icon"
      )
      const itemContext = this.parseJSON(itemEl.getAttribute("data-wp-context"))
      const openByDefault =
        typeof itemContext?.openByDefault === "boolean"
          ? itemContext.openByDefault
          : button.getAttribute("aria-expanded") === "true"

      const itemData = {
        root: itemEl,
        button,
        panel,
        icon,
        handlers: {
          click: (event) => {
            event.preventDefault()
            this.toggleItem(index)
          },
          keydown: (event) => {
            this.handleKeyDown(event, index)
          }
        },
        icons: this.getIconLabels(icon),
        isOpen: false,
        isAnimating: false
      }

      this.items.push(itemData)

      // Remove default WP interactivity attributes to avoid conflicts
      this.cleanInteractivityAttributes(itemEl, button, panel)

      button.addEventListener("click", itemData.handlers.click)
      button.addEventListener("keydown", itemData.handlers.keydown)

      this.setItemState(this.items.length - 1, openByDefault, {
        animate: false
      })
    })

    this.el.dataset.accordionReady = "true"
  }

  destroy() {
    this.items.forEach((item) => {
      item.button.removeEventListener("click", item.handlers.click)
      item.button.removeEventListener("keydown", item.handlers.keydown)
    })
    this.items = []
  }

  parseRootOptions() {
    const context = this.parseJSON(this.el.getAttribute("data-wp-context"))
    if (typeof context?.autoclose === "boolean") {
      this.options.autoclose = context.autoclose
    }

    const durationAttr = this.el.getAttribute("data-accordion-duration")
    const parsedDuration = parseInt(durationAttr, 10)
    if (!Number.isNaN(parsedDuration) && parsedDuration >= 0) {
      this.options.animationDuration = parsedDuration
    }
  }

  parseJSON(rawValue) {
    if (!rawValue || typeof rawValue !== "string") return null
    try {
      return JSON.parse(rawValue)
    } catch (error) {
      console.warn("Failed to parse accordion JSON context", error)
      return null
    }
  }

  cleanInteractivityAttributes(itemEl, button, panel) {
    itemEl.removeAttribute("data-wp-class--is-open")
    itemEl.removeAttribute("data-wp-init")
    itemEl.removeAttribute("data-wp-context")

    button.removeAttribute("data-wp-on--click")
    button.removeAttribute("data-wp-on--keydown")
    button.removeAttribute("data-wp-bind--aria-expanded")
    button.removeAttribute("data-wp-context")

    panel.removeAttribute("data-wp-bind--inert")
    panel.removeAttribute("data-wp-context")
  }

  async toggleItem(index) {
    const item = this.items[index]
    if (!item || item.isAnimating) return

    const shouldOpen = !item.isOpen

    if (shouldOpen && this.options.autoclose) {
      const promises = this.items
        .map((otherItem, otherIndex) => {
          if (otherIndex === index || !otherItem.isOpen) return null
          return this.setItemState(otherIndex, false)
        })
        .filter(Boolean)

      await Promise.all(promises)
    }

    await this.setItemState(index, shouldOpen)
  }

  async setItemState(index, shouldOpen, options = {}) {
    const item = this.items[index]
    if (!item) return

    const { animate = true } = options

    if (item.isAnimating) return

    const nextState = Boolean(shouldOpen)
    const stateChanged = item.isOpen !== nextState

    item.isAnimating = true

    item.button.setAttribute("aria-expanded", nextState ? "true" : "false")
    item.panel.setAttribute("aria-hidden", nextState ? "false" : "true")
    item.root.classList.toggle("is-open", nextState)
    this.syncIcon(item, nextState)

    if (nextState) {
      item.panel.removeAttribute("inert")
      item.panel.removeAttribute("hidden")
      if (stateChanged && animate) {
        await DOMAnimations.slideDown(
          item.panel,
          this.options.animationDuration
        )
      }
      this.syncPanelVisibility(item, true)
    } else {
      item.panel.setAttribute("inert", "")
      if (stateChanged && animate) {
        await DOMAnimations.slideUp(
          item.panel,
          this.options.animationDuration
        )
      }
      this.syncPanelVisibility(item, false)
    }

    item.isOpen = nextState
    item.isAnimating = false
  }

  syncPanelVisibility(item, isOpen) {
    if (isOpen) {
      item.panel.style.removeProperty("display")
      item.panel.removeAttribute("hidden")
    } else {
      item.panel.style.display = "none"
      item.panel.setAttribute("hidden", "")
    }
  }

  syncIcon(item, isOpen) {
    if (!item.icon) return
    const nextValue = isOpen ? item.icons.open : item.icons.closed
    if (typeof nextValue === "string" && nextValue.length) {
      item.icon.textContent = nextValue
    }
  }

  getIconLabels(icon) {
    if (!icon) {
      return { open: "", closed: "" }
    }

    const closed = icon.dataset.iconClosed
      ? icon.dataset.iconClosed
      : icon.getAttribute("data-icon-closed") ?? icon.textContent.trim()

    const open =
      icon.dataset.iconOpen ??
      icon.getAttribute("data-icon-open") ??
      closed

    return {
      open,
      closed
    }
  }

  handleKeyDown(event, index) {
    const { key } = event

    switch (key) {
      case "Enter":
      case " ":
        event.preventDefault()
        this.toggleItem(index)
        break
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault()
        this.focusItem(this.getNextIndex(index))
        break
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault()
        this.focusItem(this.getPreviousIndex(index))
        break
      case "Home":
        event.preventDefault()
        this.focusItem(0)
        break
      case "End":
        event.preventDefault()
        this.focusItem(this.items.length - 1)
        break
      default:
        break
    }
  }

  focusItem(index) {
    const item = this.items[index]
    if (item?.button) {
      item.button.focus()
    }
  }

  getNextIndex(current) {
    return (current + 1) % this.items.length
  }

  getPreviousIndex(current) {
    return (current - 1 + this.items.length) % this.items.length
  }
}

export default AccordionBlock